import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ViewChild,
  ElementRef,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss']
})
export class DropdownComponent implements OnInit, OnChanges {
  private static currentOpen: DropdownComponent | null = null;

  constructor(private elementRef: ElementRef) {}
  @Input() label: string = '';
  @Input() placeholder: string = 'Select...';
  @Input() required: boolean = false;
  @Input() labelPosition: 'top' | 'left' = 'top';
  @Input() items: any[] = [];
  @Input() selectedItem: any = null;
  @Input() selectedItems: any[] = [];
  @Input() displayField: string = 'name';
  @Input() valueField: string = 'id';
  @Input() selectFirstByDefault: boolean = false;
  @Input() multiSelect: boolean = false;
  @Input() multiSelectItemLabel: string = 'selected';
  @Input() showNamesInTrigger: boolean = false;
  @Input() showSelectAll: boolean = true;
  @Input() showClear: boolean = false;
  @Input() error: boolean = false;
  @Input() errorMessage: string = '';
  @Input() searchable: boolean = false;

  @Output() selectionChange = new EventEmitter<any>();
  @Output() multiSelectionChange = new EventEmitter<any[]>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() scrollEnd = new EventEmitter<void>();

  @ViewChild('dropdownMenu') dropdownMenu: ElementRef | null = null;
  @ViewChild('triggerRef') triggerRef: ElementRef | null = null;
  @ViewChild('searchInput') searchInputRef: ElementRef | null = null;

  isOpen = false;
  isOverflowOpen = false;
  highlightedIndex = -1;
  menuTop = 0;
  menuLeft = 0;
  menuWidth = 0;
  searchQuery: string = '';
  filteredItems: any[] = [];
  private firstItemSelected = false;

  ngOnInit() {
    if (!this.items) { this.items = []; }
    if (!this.selectedItems) { this.selectedItems = []; }
    this.filteredItems = this.items;

    if (!this.multiSelect && this.selectedItem && this.items.length > 0) {
      const isValid = this.items.some(i =>
        i === this.selectedItem ||
        (typeof i === 'object' && i[this.valueField] === this.selectedItem)
      );
      if (!isValid) {
        this.selectedItem = null;
      }
    }

    this.selectFirstItemIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items']) {
      if (!this.items) { this.items = []; }
      if (this.searchQuery && this.searchQuery.trim()) {
        const lower = this.searchQuery.toLowerCase();
        this.filteredItems = this.items.filter(item => {
          const val = typeof item === 'object' ? item[this.displayField] : String(item);
          return val && val.toLowerCase().indexOf(lower) !== -1;
        });
      } else {
        this.filteredItems = this.items;
      }
      this.selectFirstItemIfNeeded();
    }
    if (changes['selectedItems']) {
      this.selectedItems = changes['selectedItems'].currentValue || [];
    }
  }

  private selectFirstItemIfNeeded(): void {
    if (this.selectFirstByDefault && !this.multiSelect && !this.selectedItem && this.items.length > 0 && !this.firstItemSelected) {
      this.selectedItem = this.items[0];
      this.firstItemSelected = true;
      this.selectionChange.emit(this.selectedItem);
    }
  }

  private calculateMenuPosition(): void {
    if (!this.triggerRef) { return; }
    const rect = this.triggerRef.nativeElement.getBoundingClientRect();
    this.menuTop = rect.bottom;
    this.menuLeft = rect.left;
    this.menuWidth = rect.width;
  }

  toggleDropdown(event?: Event): void {
    const opening = !this.isOpen;
    if (opening && DropdownComponent.currentOpen && DropdownComponent.currentOpen !== this) {
      DropdownComponent.currentOpen.closeDropdown();
    }
    this.isOpen = opening;
    DropdownComponent.currentOpen = this.isOpen ? this : null;
    this.highlightedIndex = -1;
    if (this.isOpen) {
      this.calculateMenuPosition();
      this.searchQuery = '';
      this.filteredItems = this.items || [];
      if (this.searchable) {
        setTimeout(() => {
          if (this.searchInputRef) {
            this.searchInputRef.nativeElement.focus();
          }
        }, 50);
      }
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.isOverflowOpen = false;
    this.highlightedIndex = -1;
    this.searchQuery = '';
    this.filteredItems = this.items || [];
    if (DropdownComponent.currentOpen === this) {
      DropdownComponent.currentOpen = null;
    }
  }

  toggleOverflowDropdown(event: Event): void {
    event.stopPropagation();
    this.isOverflowOpen = !this.isOverflowOpen;
    if (this.isOverflowOpen) {
      this.isOpen = false;
    }
  }

  getVisibleSelectedItems(): any[] {
    return this.selectedItems.slice(0, 2);
  }

  getExtraSelectedCount(): number {
    return Math.max(0, this.selectedItems.length - 2);
  }

  getOverflowSelectedItems(): any[] {
    return this.selectedItems.slice(2);
  }

  selectItem(item: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.multiSelect) {
      const index = this.selectedItems.indexOf(item);
      if (index > -1) {
        this.selectedItems = this.selectedItems.filter((_, i) => i !== index);
      } else {
        this.selectedItems = [...this.selectedItems, item];
      }
      this.multiSelectionChange.emit(this.selectedItems);
    } else {
      this.selectedItem = item;
      this.selectionChange.emit(item);
      this.closeDropdown();
    }
  }

  toggleSelectAll(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.isAllSelected()) {
      this.selectedItems = [];
    } else {
      this.selectedItems = [...(this.filteredItems || [])];
    }
    this.multiSelectionChange.emit(this.selectedItems);
  }

  clearSelection(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedItems = [];
    this.multiSelectionChange.emit(this.selectedItems);
  }

  isAllSelected(): boolean {
    const list = this.filteredItems || [];
    const selected = this.selectedItems || [];
    return list.length > 0 && list.every(item => selected.indexOf(item) !== -1);
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const query = input.value;
    this.searchQuery = query;
    this.searchChange.emit(query);
    if (!query.trim()) {
      this.filteredItems = this.items || [];
      return;
    }
    const lower = query.toLowerCase();
    this.filteredItems = (this.items || []).filter(item => {
      const val = typeof item === 'object' ? item[this.displayField] : String(item);
      return val && val.toLowerCase().indexOf(lower) !== -1;
    });
  }

  getDisplayValue(): string {
    if (!this.multiSelect) {
      if (!this.selectedItem) {
        return '';
      }
      if (typeof this.selectedItem !== 'object') {
        const list = this.items || [];
        const match = list.find(
          i => typeof i === 'object' && i[this.valueField] === this.selectedItem
        );
        return match ? (match[this.displayField] || String(this.selectedItem)) : String(this.selectedItem);
      }
      return this.selectedItem[this.displayField] || '';
    } else {
      const selected = this.selectedItems || [];
      if (selected.length === 0) {
        return '';
      }
      if (selected.length === 1) {
        return typeof selected[0] === 'object'
          ? selected[0][this.displayField] || ''
          : selected[0];
      }
      if (this.showNamesInTrigger) {
        return this.getSelectedNames();
      }
      return `${selected.length} ${this.multiSelectItemLabel}`;
    }
  }

  getSelectedNames(): string {
    const selected = this.selectedItems || [];
    const names = selected.map((s: any) => (typeof s === 'object' ? s[this.displayField] : s));
    const visible = 2;
    if (names.length <= visible) {
      return names.join(', ');
    }
    return names.slice(0, visible).join(', ') + ' +' + (names.length - visible) + ' more';
  }

  isItemSelected(item: any): boolean {
    if (!this.multiSelect) {
      if (!this.selectedItem) {
        return false;
      }
      if (typeof this.selectedItem !== 'object' && typeof item === 'object') {
        return item[this.valueField] === this.selectedItem;
      }
      return this.selectedItem === item;
    } else {
      return this.selectedItems.includes(item);
    }
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
      this.scrollEnd.emit();
    }
  }

  onMouseEnter(index: number): void {
    this.highlightedIndex = index;
  }

  onMouseLeave(): void {
    this.highlightedIndex = -1;
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    if (this.isOpen) {
      this.closeDropdown();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    if (this.isOpen) {
      this.closeDropdown();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen && !this.isOverflowOpen) {
      return;
    }
    const target = event.target as HTMLElement;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.closeDropdown();
    }
  }
}
